import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SidebarTrigger } from '../components/ui/sidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Loader2, KeyRound, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import ApiService from '../services/api.service';
import { useAppAuth } from '@/hooks/useAppAuth';

interface DestinationStatus {
  tenant_slug: string;
  database: string;
  fivetran_destination_id: string | null;
  loader_auth_method: string;
  has_private_key: boolean;
  can_upgrade: boolean;
}

function useDestinationStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['fivetranDestinationStatus'],
    queryFn: () =>
      ApiService.getInstance().request(
        'GET',
        '/danger-zone/fivetran-destination-status',
      ) as Promise<DestinationStatus>,
    enabled,
    staleTime: 0,
  });
}

function StatusRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-mono text-foreground">{value}</span>
    </div>
  );
}

export default function FivetranKeypairUpgrade() {
  const queryClient = useQueryClient();
  const { user } = useAppAuth();

  const isAdmin = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.some(
      (r: any) =>
        (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase() === 'admin',
    );
  }, [user]);

  const { data: status, isLoading, isError, error } = useDestinationStatus(isAdmin);

  const [dialogOpen, setDialogOpen] = useState(false);

  const verifyMutation = useMutation({
    mutationFn: () =>
      ApiService.getInstance().request('POST', '/danger-zone/verify-fivetran-keypair') as Promise<{
        ok: boolean;
        authenticated_user: string;
      }>,
    onSuccess: (result) => {
      toast.success('Key-pair authenticated', {
        description: `Connected to Snowflake as ${result.authenticated_user}`,
      });
    },
    onError: (err: any) => {
      toast.error('Key-pair check failed', { description: err?.message || 'An error occurred' });
    },
  });

  const upgradeMutation = useMutation({
    mutationFn: () =>
      ApiService.getInstance().request('POST', '/danger-zone/upgrade-fivetran-keypair', {
        confirm: true,
      }) as Promise<{ ok: boolean; tenant_slug: string; fivetran_destination_id: string }>,
    onSuccess: (result) => {
      toast.success('Destination upgraded to key-pair', {
        description: `Tenant ${result.tenant_slug} (destination ${result.fivetran_destination_id})`,
      });
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['fivetranDestinationStatus'] });
    },
    onError: (err: any) => {
      toast.error('Upgrade failed', { description: err?.message || 'An error occurred' });
    },
  });

  if (!isAdmin) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-semibold text-foreground mb-2">Fivetran Key-Pair Upgrade</h1>
        <p className="text-muted-foreground">You need Admin access to use this page.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex items-center gap-1">
          <SidebarTrigger className="md:hidden h-5 w-5" />
          <h1 className="text-xl font-semibold text-foreground">Fivetran Key-Pair Upgrade</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-muted border border-border">
            <KeyRound className="h-5 w-5 text-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-semibold text-foreground">Migrate the Snowflake destination to key-pair auth</p>
              <p className="mt-1">
                Snowflake is shutting off single-factor password auth for programmatic users.
                This switches the current tenant's Fivetran Snowflake destination to use the
                key-pair already provisioned on the loader user.
              </p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Current Tenant Destination</CardTitle>
              <CardDescription>Loader credentials and Fivetran destination for the tenant you're in.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading status…
                </div>
              )}
              {isError && (
                <div className="text-sm text-destructive">
                  Failed to load status: {(error as any)?.message || 'unknown error'}
                </div>
              )}
              {status && (
                <div className="space-y-1">
                  <StatusRow label="Tenant" value={status.tenant_slug} />
                  <StatusRow label="Database" value={status.database} />
                  <StatusRow
                    label="Fivetran destination"
                    value={status.fivetran_destination_id || '— none —'}
                  />
                  <StatusRow label="Loader auth method" value={status.loader_auth_method} />
                  <StatusRow label="Has private key" value={status.has_private_key ? 'yes' : 'no'} />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                Upgrade to Key-Pair
              </CardTitle>
              <CardDescription>
                Re-points the Fivetran Snowflake destination at the loader user's key-pair.
                Test the key first to confirm Snowflake accepts it. Safe to re-run.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {status && !status.can_upgrade && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    This tenant can't be upgraded right now — it needs to be a Snowflake tenant
                    with an existing Fivetran destination and key-pair credentials on the loader user.
                  </span>
                </div>
              )}

              {verifyMutation.isSuccess && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Snowflake authenticated the key-pair as{' '}
                    <span className="font-mono">{verifyMutation.data?.authenticated_user}</span>.
                  </span>
                </div>
              )}
              {verifyMutation.isError && (
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{(verifyMutation.error as any)?.message || 'Key-pair check failed.'}</span>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => verifyMutation.mutate()}
                  disabled={!status?.can_upgrade || verifyMutation.isPending || upgradeMutation.isPending}
                  className="flex-1"
                >
                  {verifyMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testing…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Test Key-Pair
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setDialogOpen(true)}
                  disabled={!status?.can_upgrade || upgradeMutation.isPending}
                  className="flex-1"
                >
                  {upgradeMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Upgrading…
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4 mr-2" />
                      Upgrade to Key-Pair
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                The upgrade also re-runs this auth check on the server and aborts before touching
                Fivetran if it fails.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !upgradeMutation.isPending && setDialogOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade destination to key-pair?</DialogTitle>
            <DialogDescription>
              This will update the Fivetran Snowflake destination for tenant{' '}
              <span className="font-mono font-semibold">{status?.tenant_slug}</span> to use key-pair
              authentication. Existing syncs will use the new credentials on their next run.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={upgradeMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => upgradeMutation.mutate()} disabled={upgradeMutation.isPending}>
              {upgradeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Upgrading…
                </>
              ) : (
                'Confirm Upgrade'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
